import {
	AcknowledgementCheckbox,
	AcknowledgementCheckboxText,
	RadioGroupContainer,
	SaveButton
} from "components/Preferences/UserSettings/CommunicationSettings.styles.ts";
import { Checkbox, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { SetStateAction, useState } from "react";
import AcknowledgementLinePreview from "components/Preferences/AcknowledgementLinePreview.tsx";
import { models } from "types/api/viewModels.ts";
import { useUpdatePreferencesMutation } from "features/api/preferencesApi.ts";
import { UserSettingsContainer } from "components/Preferences/UserSettings/userSettings.styles.ts";
import {
	AcknowledgementCondensedViewLabel,
	AcknowledgementExpandedViewLabel,
	CommunicationSettingsText,
	NewUserPreferenceSaveErrorText,
	ReceiveAcknowledgementEmailsDescriptionText,
	ReceiveAcknowledgementEmailsOptionText,
	SavingUserPreferenceText,
	UserPreferenceSuccessfullySavedText
} from "components/Preferences/text.ts";
import { AcknowledgmentEmailFormat } from "types/declarations/enums.ts";
import Snackbar from "../../Common/SnackBar/SnackBar.tsx";
import useSnackbarEffect from "hooks/useSnackbarEffect.ts";

export interface CommunicationSettingsProps {
	userPreferences?: models["UserPreferencesViewModel"];
	userPermissions?: models["UserPermissionsViewModel"];
}

const CommunicationSettings = ({ userPreferences, userPermissions }: CommunicationSettingsProps) => {
	const [isChecked, setIsChecked] = useState<boolean>(userPreferences?.receiveAcknowledgementEmails ?? false);
	const [radioValue, setRadioValue] = useState<models["AcknowledgmentEmailFormat"]>(
		userPreferences?.acknowledgementEmailFormat ?? AcknowledgmentEmailFormat.EXPANDED
	);
	const [updatePreferences, { isLoading: isUpdateLoading, isError: isUpdateError }] = useUpdatePreferencesMutation();

	const { snackbarOpen } = useSnackbarEffect(isUpdateLoading);

	const handleOnChecked = (event: SetStateAction<any>) => {
		setIsChecked(!isChecked);
	};

	const handleOnRadioChanged = (event: SetStateAction<any>, value: any) => {
		setRadioValue(value);
	};

	const handleUpdatePreferences = async () => {
		try {
			const request = {
				receiveAcknowledgementEmails: isChecked,
				acknowledgementEmailFormat: radioValue
			};
			await updatePreferences(request).unwrap();
		} catch (error: any) {
			console.error(error);
		}
	};

	const checkBoxLabel = () => {
		return (
			<AcknowledgementCheckboxText>
				<div>{ReceiveAcknowledgementEmailsOptionText}</div>
				<div>{ReceiveAcknowledgementEmailsDescriptionText}</div>
			</AcknowledgementCheckboxText>
		);
	};

	return (
		<UserSettingsContainer>
			<div>{CommunicationSettingsText}</div>
			<div>
				<AcknowledgementCheckbox
					control={
						<Checkbox
							size="medium"
							checked={isChecked}
							onChange={handleOnChecked}
						/>
					}
					label={checkBoxLabel()}
					labelPlacement="end"
				/>
				{isChecked && (
					<>
						<RadioGroupContainer>
							<RadioGroup
								value={radioValue}
								onChange={handleOnRadioChanged}
							>
								<FormControlLabel
									control={<Radio />}
									value={AcknowledgmentEmailFormat.CONDENSED}
									label={AcknowledgementCondensedViewLabel}
								/>
								<FormControlLabel
									control={<Radio />}
									value={AcknowledgmentEmailFormat.EXPANDED}
									label={AcknowledgementExpandedViewLabel}
								/>
							</RadioGroup>
						</RadioGroupContainer>
						<AcknowledgementLinePreview
							priceType={userPermissions?.acknowledgementPriceFormat}
							layout={radioValue}
						/>
					</>
				)}
			</div>
			<SaveButton
				variant="contained"
				color="primary"
				size="large"
				onClick={handleUpdatePreferences}
			>
				Save
			</SaveButton>
			<Snackbar
				open={snackbarOpen}
				isLoading={isUpdateLoading}
				loadingText={SavingUserPreferenceText}
				successText={UserPreferenceSuccessfullySavedText}
				isError={isUpdateError}
				errorText={NewUserPreferenceSaveErrorText}
			/>
		</UserSettingsContainer>
	);
};

export default CommunicationSettings;
